import { createQuery, createUrl } from '../helpers'
import {
  DEFAULT_REQUEST_OPTIONS,
  DEFAULT_LIST_REQUEST_OPTIONS
} from '../config'
import type { ApiClient } from '../../index'
import type {
  DeepPartial,
  Id,
  PathOptions,
  Path,
  ListQuery,
  ListRequestOptionsNoQuery,
  ListOptions
} from '../types'

export const listWrapper = <
  K extends string,
  T extends { [key in K]: Id },
  PathO extends PathOptions = PathOptions
>(
  apiClient: ApiClient,
  receivedOptions: ListOptions<K, PathO>
) => {
  const baseRequestOptions = (() => {
    const mergedOptions = Object.assign(
      {},
      DEFAULT_REQUEST_OPTIONS,
      DEFAULT_LIST_REQUEST_OPTIONS,
      receivedOptions.requestOptions
    )

    delete mergedOptions.query
    return mergedOptions as Omit<typeof mergedOptions, 'query'>
  })()

  const baseQuery = Object.assign(
    {},
    DEFAULT_REQUEST_OPTIONS.query,
    DEFAULT_LIST_REQUEST_OPTIONS.query,
    receivedOptions.requestOptions?.query
  )

  return {
    idKey: receivedOptions.idKey,

    getItems: async <U extends Pick<T, K> & DeepPartial<Omit<T, K>> = T>(
      requestQuery: ListQuery = {},
      pathOptions?: PathO,
      requestOptions: ListRequestOptionsNoQuery = {}
    ) => {
      const options = Object.assign({}, baseRequestOptions, requestOptions)
      const mergedQuery = Object.assign({}, baseQuery, requestQuery)

      const query = createQuery(mergedQuery)
      const url = createUrl(
        receivedOptions.path as Path,
        requestQuery,
        pathOptions ?? {}
      )

      const result = await apiClient.get<U[] | null>(
        url,
        { query },
        { cache: options.cache }
      )

      return result ?? []
    }
  }
}

export type ListWrapperPublicInterface = ReturnType<typeof listWrapper>
